import React, { useState } from 'react';
import { useRecordContext, useRefresh, useNotify } from 'react-admin';
import PublicIcon from '@mui/icons-material/Public';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import IconButton from '../common/buttons/IconButton';
import { checkAddressPeppol } from '../../services/api';

const CheckPeppolButton = () => {
    const record = useRecordContext();
    const refresh = useRefresh();
    const notify = useNotify();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleCheck = async () => {
        setLoading(true);
        setError(null);
        try {
            if (!record?.vat_number) {
                setError('Address has no VAT number');
                notify('Address has no VAT number', { type: 'error' });
                setLoading(false);
                return;
            }

            const data = await checkAddressPeppol(record?.id);
            setResult(data);

            if (data.success === false) {
                setError(data.message || 'Error checking PEPPOL status');
                notify('Error checking PEPPOL status', { type: 'error' });
            } else if (data.id) {
                // Successful response - API returns the address object with id field
                const message = data.is_present_on_peppol
                    ? 'Address found on PEPPOL ✓'
                    : 'Address not found on PEPPOL';
                notify(message, { type: data.is_present_on_peppol ? 'info' : 'warning' });
            } else {
                setError('Unexpected response from server');
                notify('Unexpected response from server', { type: 'error' });
            }

            refresh();
        } catch (err) {
            setError(err.message || 'Error checking PEPPOL status');
            notify('Error checking PEPPOL status', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = async () => {
        setOpen(true);
        await handleCheck();
    };

    const handleClose = () => {
        setOpen(false);
        setResult(null);
        setError(null);
    };

    return (
        <>
            <IconButton icon={<PublicIcon />} onClick={handleOpen}>
                Check PEPPOL
            </IconButton>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>PEPPOL Status Check</DialogTitle>
                <DialogContent sx={{ minHeight: '200px' }}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">{error}</Alert>
                    ) : result ? (
                        <Box sx={{ py: 2 }}>
                            {result.is_present_on_peppol ? (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    <strong>Address found on PEPPOL ✓</strong>
                                </Alert>
                            ) : (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <strong>Address not found on PEPPOL</strong>
                                </Alert>
                            )}

                            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                <Typography variant="body2" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                                    <strong>PEPPOL ID:</strong> {result.peppol_id || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1, color: '#333', fontWeight: 500 }}>
                                    <strong>VAT Number:</strong> {result.vat_number}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                                    <strong>Checked at:</strong> {result.peppol_checked_at ? new Date(result.peppol_checked_at).toLocaleString() : 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CheckPeppolButton;
